import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TicketsService, UnidadesService } from '@/services';
import {
  Plus,
  Search,
  Eye,
  Ticket,
  AlertCircle,
  Clock,
  CheckCircle,
  Calendar,
  Paperclip,
  Filter,
  X,
  AtSign,
  Copy,
  AlertTriangle
} from 'lucide-react';
import { Ticket as TicketType, Unidade, BuscaAvancadaFiltros } from '@/types';
import toast from 'react-hot-toast';
import UnifiedFiltersModal from '@/components/ui/UnifiedFiltersModal';
import FavoriteButton from '@/components/FavoriteButton';

interface Filters {
  status: string;
  prioridade: string;
  unidade: string;
  departamento?: string;
  usuario?: string;
  assunto?: string;
  temAnexos?: string;
  temNotificacoes?: string;
  ehFavorito?: string;
  dataCriacaoInicio?: string;
  dataCriacaoFim?: string;
  dataAtualizacaoInicio?: string;
  dataAtualizacaoFim?: string;
  ordenarPor?: string;
  ordenarDirecao?: 'asc' | 'desc';
  tags?: string[];
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

interface StatusSummary {
  abertos: number;
  emAndamento: number;
  fechados: number;
}

export default function Tickets() {
  const { usuario } = useAuth();
  const [tickets, setTickets] = useState<TicketType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [ticketsComNotificacoes, setTicketsComNotificacoes] = useState<Set<number>>(new Set());
  const [ticketsFavoritos, setTicketsFavoritos] = useState<Set<number>>(new Set());
  const [filtrosBuscaAvancada, setFiltrosBuscaAvancada] = useState<BuscaAvancadaFiltros>({});
  const [filtersMode, setFiltersMode] = useState<'basic' | 'advanced'>('basic');
  const [filters, setFilters] = useState<Filters>({
    status: 'all',
    prioridade: 'all',
    unidade: 'all',
    departamento: 'all',
    usuario: 'all',
    assunto: 'all',
    temAnexos: 'all',
    temNotificacoes: 'all',
    ehFavorito: 'all',
    ordenarPor: 'created_at',
    ordenarDirecao: 'desc'
  });
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState<'todos' | 'mencoes'>('todos');
  const [statusSummary, setStatusSummary] = useState<StatusSummary>({
    abertos: 0,
    emAndamento: 0,
    fechados: 0,
  });
  const [totalDuplicados, setTotalDuplicados] = useState<number>(0);
  const [totalComNotificacoes, setTotalComNotificacoes] = useState<number>(0);
  const [totalTodos, setTotalTodos] = useState<number>(0);
  const [totalMencoes, setTotalMencoes] = useState<number>(0);
  const [quickFilter, setQuickFilter] = useState<'all' | 'unread' | 'duplicados'>('all');
  const [usuariosFiltro, setUsuariosFiltro] = useState<Array<{ id: number; nome: string }>>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Helper: ler filtros da URL (prioridade para voltar/recarregar)
  const getFiltersFromUrl = useCallback((): Partial<Filters> => {
    const url = searchParams;
    const f: Partial<Filters> = {};
    const status = url.get('status');
    if (status) f.status = status;
    const prioridade = url.get('prioridade');
    if (prioridade) f.prioridade = prioridade;
    const unidade = url.get('unidade');
    if (unidade) f.unidade = unidade;
    const departamento = url.get('departamento');
    if (departamento) f.departamento = departamento;
    const usuarioId = url.get('usuario');
    if (usuarioId) f.usuario = usuarioId;
    const assunto = url.get('assunto');
    if (assunto) f.assunto = assunto;
    const temAnexos = url.get('temAnexos');
    if (temAnexos) f.temAnexos = temAnexos;
    const temNotificacoes = url.get('temNotificacoes');
    if (temNotificacoes) f.temNotificacoes = temNotificacoes;
    const ehFavorito = url.get('ehFavorito');
    if (ehFavorito) f.ehFavorito = ehFavorito;
    const dataCriacaoInicio = url.get('dataCriacaoInicio');
    if (dataCriacaoInicio) f.dataCriacaoInicio = dataCriacaoInicio;
    const dataCriacaoFim = url.get('dataCriacaoFim');
    if (dataCriacaoFim) f.dataCriacaoFim = dataCriacaoFim;
    const ordenarPor = url.get('ordenarPor');
    if (ordenarPor) f.ordenarPor = ordenarPor;
    const ordenarDirecao = url.get('ordenarDirecao');
    if (ordenarDirecao === 'asc' || ordenarDirecao === 'desc') f.ordenarDirecao = ordenarDirecao;
    const tagsStr = url.get('tags');
    if (tagsStr) f.tags = tagsStr.split(',').filter(Boolean);
    return f;
  }, [searchParams]);

  useEffect(() => {
    if (usuario) {
      // Ler da URL primeiro (prioridade para voltar/recarregar)
      const urlPage = searchParams.get('page');
      const urlSearch = searchParams.get('search');
      const urlTab = searchParams.get('tab');
      const urlQuick = searchParams.get('quick');
      if (urlPage) {
        const p = parseInt(urlPage, 10);
        if (p >= 1) setPagination(prev => ({ ...prev, currentPage: p }));
      }
      if (urlSearch !== null) setSearchTerm(urlSearch || '');
      if (urlTab === 'mencoes' || urlTab === 'todos') setActiveTab(urlTab);
      if (urlQuick === 'unread' || urlQuick === 'duplicados') setQuickFilter(urlQuick);
      
      const urlFilters = getFiltersFromUrl();
      const hasUrlFilters = Object.keys(urlFilters).length > 0;
      
      const defaultFilters: Filters = {
        status: 'all',
        prioridade: 'all',
        unidade: 'all',
        departamento: 'all',
        usuario: 'all',
        assunto: 'all',
        temAnexos: 'all',
        temNotificacoes: 'all',
        ehFavorito: 'all',
        ordenarPor: 'created_at',
        ordenarDirecao: 'desc'
      };
      
      const hasMeaningfulParams = hasUrlFilters || urlPage || urlSearch !== null || urlTab === 'mencoes' || urlQuick !== null;
      if (hasMeaningfulParams) toast.success('Os filtros foram mantidos para uma melhor experiência', { id: 'filters-restored' });

      const filtersToUse = hasUrlFilters
        ? { ...defaultFilters, ...urlFilters }
        : (() => {
            const savedFilters = localStorage.getItem(`tickets_filters_${usuario.id}`);
            if (savedFilters) {
              try {
                const parsedFilters = JSON.parse(savedFilters);
                parsedFilters.temNotificacoes = 'all';
                return { ...defaultFilters, ...parsedFilters };
              } catch (e) {
                console.error('Erro ao parsear filtros do localStorage:', e);
              }
            }
            return defaultFilters;
          })();
      
      setFilters(filtersToUse);
      
      const savedSearchTerm = localStorage.getItem(`tickets_search_${usuario.id}`);
      if (savedSearchTerm && urlSearch === null) {
        setSearchTerm(savedSearchTerm);
      }
      
      const savedTab = localStorage.getItem(`tickets_tab_${usuario.id}`);
      if ((savedTab === 'mencoes' || savedTab === 'todos') && urlTab === null) {
        setActiveTab(savedTab);
      }
      
      loadUnidades();
      loadTickets(urlPage ? parseInt(urlPage, 10) : 1, undefined, filtersToUse);
    }
  }, [usuario?.id]);

  useEffect(() => {
    if (usuario && !isInitialLoad) {
      localStorage.setItem(`tickets_filters_${usuario.id}`, JSON.stringify(filters));
    }
  }, [filters, usuario, isInitialLoad]);

  useEffect(() => {
    if (usuario) {
      localStorage.setItem(`tickets_search_${usuario.id}`, searchTerm);
    }
  }, [searchTerm, usuario]);

  useEffect(() => {
    if (usuario) {
      localStorage.setItem(`tickets_tab_${usuario.id}`, activeTab);
    }
  }, [activeTab, usuario]);

  // Sincronizar estado com URL (filtros e paginação via GET) - mantém consistência ao voltar/recarregar
  useEffect(() => {
    if (!usuario || isInitialLoad) return;
    const params = new URLSearchParams();
    if (pagination.currentPage > 1) params.set('page', String(pagination.currentPage));
    if (searchTerm) params.set('search', searchTerm);
    params.set('tab', activeTab);
    if (quickFilter !== 'all') params.set('quick', quickFilter);
    if (filters.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters.prioridade && filters.prioridade !== 'all') params.set('prioridade', filters.prioridade);
    if (filters.unidade && filters.unidade !== 'all') params.set('unidade', filters.unidade);
    if (filters.departamento && filters.departamento !== 'all') params.set('departamento', filters.departamento);
    if (filters.usuario && filters.usuario !== 'all') params.set('usuario', filters.usuario);
    if (filters.assunto && filters.assunto !== 'all') params.set('assunto', filters.assunto);
    if (filters.temAnexos && filters.temAnexos !== 'all') params.set('temAnexos', filters.temAnexos);
    if (filters.temNotificacoes && filters.temNotificacoes !== 'all') params.set('temNotificacoes', filters.temNotificacoes);
    if (filters.ehFavorito && filters.ehFavorito !== 'all') params.set('ehFavorito', filters.ehFavorito);
    if (filters.dataCriacaoInicio) params.set('dataCriacaoInicio', filters.dataCriacaoInicio);
    if (filters.dataCriacaoFim) params.set('dataCriacaoFim', filters.dataCriacaoFim);
    if (filters.ordenarPor && filters.ordenarPor !== 'created_at') params.set('ordenarPor', filters.ordenarPor);
    if (filters.ordenarDirecao && filters.ordenarDirecao !== 'desc') params.set('ordenarDirecao', filters.ordenarDirecao);
    if (filters.tags && filters.tags.length > 0) params.set('tags', filters.tags.join(','));
    setSearchParams(params, { replace: true });
  }, [pagination.currentPage, searchTerm, activeTab, quickFilter, filters, usuario, isInitialLoad]);

  // Recarregar tickets quando filtros, searchTerm ou activeTab mudarem (com debounce)
  useEffect(() => {
    if (usuario && !isInitialLoad) {
      const timeoutId = setTimeout(() => {
        loadTickets(1);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [filters, searchTerm, activeTab, usuario?.exibir_fechados]);


  const loadTickets = async (page: number = 1, itemsPerPage?: number, filtersOverride?: Filters) => {
    try {
      setIsLoading(true);
      const limit = itemsPerPage ?? pagination.itemsPerPage;
      const offset = (page - 1) * limit;
      const f = filtersOverride ?? filters;

      // Converter filtros do frontend para o formato do backend
      const backendFilters: any = {
        limite: limit,
        offset: offset,
      };

      // Mapear filtros
      if (f.status && f.status !== 'all') {
        backendFilters.status = f.status;
      }
      if (f.prioridade && f.prioridade !== 'all') {
        backendFilters.prioridade = f.prioridade;
      }
      if (f.unidade && f.unidade !== 'all') {
        backendFilters.unidade = f.unidade;
      }
      if (f.departamento && f.departamento !== 'all') {
        backendFilters.departamento = f.departamento;
      }
      if (f.usuario && f.usuario !== 'all') {
        backendFilters.usuario = f.usuario;
      }
      if (f.assunto && f.assunto !== 'all') {
        backendFilters.assunto = f.assunto;
      }
      if (f.temAnexos && f.temAnexos !== 'all') {
        backendFilters.tem_anexos = f.temAnexos;
      }
      if (f.temNotificacoes && f.temNotificacoes !== 'all') {
        backendFilters.tem_notificacoes = f.temNotificacoes;
      }
      if (f.ehFavorito && f.ehFavorito !== 'all') {
        backendFilters.eh_favorito = f.ehFavorito;
      }
      if (f.dataCriacaoInicio) {
        backendFilters.data_criacao_inicio = f.dataCriacaoInicio;
      }
      if (f.dataCriacaoFim) {
        backendFilters.data_criacao_fim = f.dataCriacaoFim;
      }
      if (f.dataAtualizacaoInicio) {
        backendFilters.data_atualizacao_inicio = f.dataAtualizacaoInicio;
      }
      if (f.dataAtualizacaoFim) {
        backendFilters.data_atualizacao_fim = f.dataAtualizacaoFim;
      }
      if (f.ordenarPor) {
        backendFilters.ordenar_por = f.ordenarPor;
      }
      if (f.ordenarDirecao) {
        backendFilters.ordenar_direcao = f.ordenarDirecao;
      }
      if (f.tags && f.tags.length > 0) {
        backendFilters.tags = f.tags;
      }
      if (activeTab === 'mencoes') {
        backendFilters.eh_mencionado = 'sim';
      }
      if (searchTerm.trim()) {
        backendFilters.busca = searchTerm.trim();
      }
      // Preferência do usuário (exibir_fechados) - alterável apenas em Configurações do perfil
      backendFilters.exibir_fechados = (usuario?.exibir_fechados ?? 1) === 1 ? 'true' : 'false';

      const response = await TicketsService.getTickets(backendFilters);
      setTickets(response.tickets);
      
      const notificacoesSet = new Set<number>();
      const favoritosSet = new Set<number>();
      
      response.tickets.forEach((ticket: any) => {
        if (ticket.tem_notificacoes) {
          notificacoesSet.add(ticket.id);
        }
        if (ticket.eh_favorito) {
          favoritosSet.add(ticket.id);
        }
      });
      
      setTicketsComNotificacoes(notificacoesSet);
      setTicketsFavoritos(favoritosSet);

      if (response.resumo_status) {
        setStatusSummary({
          abertos: response.resumo_status.abertos,
          emAndamento: response.resumo_status.em_andamento,
          fechados: response.resumo_status.fechados,
        });
      }

      // Atualizar totais globais (counts do backend, não item por item)
      setTotalDuplicados(response.total_duplicados ?? 0);
      setTotalComNotificacoes(response.total_com_notificacoes ?? 0);
      setTotalTodos(response.total_todos ?? response.total ?? 0);
      setTotalMencoes(response.total_mencoes ?? 0);
      setUsuariosFiltro(Array.isArray(response.usuarios_filtro) ? response.usuarios_filtro : []);

      setPagination(prev => {
        const newTotalItems = response.total ?? prev.totalItems;
        const newItemsPerPage = limit;
        const newTotalPages = newItemsPerPage > 0
          ? Math.ceil(newTotalItems / newItemsPerPage)
          : prev.totalPages;

        return {
          ...prev,
          currentPage: page,
          itemsPerPage: newItemsPerPage,
          totalItems: newTotalItems,
          totalPages: newTotalPages,
        };
      });
    } catch (error) {
      console.error('Erro ao carregar tickets:', error);
      toast.error('Erro ao carregar tickets');
    } finally {
      setIsLoading(false);
    }
  };



  const loadUnidades = async () => {
    try {
      const response = await UnidadesService.getUnidades();
      setUnidades(response);
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
      toast.error('Erro ao carregar unidades');
    }
  };

  const hasActiveFilters = () => {
    return (
      filters.status !== 'all' || 
      filters.prioridade !== 'all' || 
      filters.unidade !== 'all' || 
      filters.departamento !== 'all' ||
      filters.usuario !== 'all' ||
      filters.assunto !== 'all' ||
      filters.temAnexos !== 'all' ||
      filters.temNotificacoes !== 'all' ||
      filters.ehFavorito !== 'all' ||
      filters.dataCriacaoInicio ||
      filters.dataCriacaoFim ||
      filters.dataAtualizacaoInicio ||
      filters.dataAtualizacaoFim ||
      (filters.tags && filters.tags.length > 0) ||
      searchTerm.trim() !== '' ||
      activeTab === 'mencoes' ||
      quickFilter !== 'all'
    );
  };

  // Filtros agora são aplicados no backend, não precisamos mais desta função

  const usuarioTemAcessoAoDepartamento = (ticket: TicketType) => {
    if (!usuario) return false;
    
    if (usuario.nivel === 'admin' || usuario.nivel === 'dev') return true;
    
    if (usuario.departamentos && ticket.departamento_id) {
      return usuario.departamentos.some(dept => dept.id === ticket.departamento_id);
    }
    
    return false;
  };

  // Busca agora é feita no backend através do filtro de assunto;
  // não precisamos de debounce adicional aqui, pois o useEffect acima já faz o controle.

  const handleAdvancedSearch = async (filtros: BuscaAvancadaFiltros) => {
    try {
      setIsLoading(true);
      setIsSearching(true);
      
      setFiltrosBuscaAvancada(filtros);
      setSearchTerm('');
      
      const response = await TicketsService.buscaAvancada(filtros);
      setTickets(response.tickets);
      
      const notificacoesSet = new Set<number>();
      const favoritosSet = new Set<number>();
      
      response.tickets.forEach((ticket: any) => {
        if (ticket.tem_notificacoes) {
          notificacoesSet.add(ticket.id);
        }
        if (ticket.eh_favorito) {
          favoritosSet.add(ticket.id);
        }
      });
      
      setTicketsComNotificacoes(notificacoesSet);
      setTicketsFavoritos(favoritosSet);
      
      toast.success(`Encontrados ${response.tickets.length} ticket(s)`);
    } catch (error) {
      console.error('Erro na busca avançada:', error);
      toast.error('Erro ao realizar busca avançada');
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  useEffect(() => {
    if (filtersMode === 'basic' && filtrosBuscaAvancada && Object.keys(filtrosBuscaAvancada).length > 0) {
      const recarregarTickets = async () => {
        await loadTickets();
        setFiltrosBuscaAvancada({});
      };
      recarregarTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersMode]);

  // Filtros agora são aplicados no backend, não precisamos mais aplicar localmente

  const totalItems = pagination.totalItems || tickets.length;
  const totalPages = pagination.totalPages || Math.ceil(totalItems / pagination.itemsPerPage);

  const getCurrentPageItems = () => {
    // Backend já retorna a página atual
    // Aqui aplicamos apenas o filtro rápido local para duplicados
    if (quickFilter === 'duplicados') {
      return tickets.filter((t) => t.eh_duplicado);
    }
    return tickets;
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    loadTickets(page);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aberto': return 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700';
      case 'Em Andamento': return 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700';
      case 'Fechado': return 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700';
      case 'Pendente': return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700';
      case 'Resolvido': return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
    }
  };

  const getPriorityColor = (prioridade: string) => {
    switch (prioridade) {
      case 'Alta': return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700';
      case 'Média': return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700';
      case 'Baixa': return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700';
      case 'Crítica': return 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700';
      case 'Urgente': return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
    }
  };

  const formatDateWithTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  };

  // Função para limpar todos os filtros
  const clearFilters = () => {
    setFilters({
      status: 'all',
      prioridade: 'all',
      unidade: 'all',
      departamento: 'all',
      usuario: 'all',
      assunto: 'all',
      temAnexos: 'all',
      temNotificacoes: 'all',
      ehFavorito: 'all',
      ordenarPor: 'created_at',
      ordenarDirecao: 'desc'
    });
    setSearchTerm('');
    setQuickFilter('all');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(new URLSearchParams(), { replace: true });
    
    if (usuario) {
      localStorage.removeItem(`tickets_filters_${usuario.id}`);
      localStorage.removeItem(`tickets_search_${usuario.id}`);
    }
  };

  // Full-page loading só na primeira carga (lista vazia); ao buscar/filtrar mantém a tela para não perder foco do input
  if (isLoading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Tickets
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Bem-vindo ao Helpdesk, {usuario?.nome}!
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar tickets (número, assunto, usuário ou conteúdo das respostas)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400" title="Buscando..."></div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFiltersModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
            </button>
            
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600"
                title="Limpar todos os filtros"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
              <Ticket className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total de Tickets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {pagination.totalItems || tickets.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tickets Abertos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {statusSummary.abertos}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
              <Clock className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Em Andamento</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {statusSummary.emAndamento}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Fechados</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {statusSummary.fechados}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('todos')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                activeTab === 'todos'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span className="flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Todos
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === 'todos'
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  {totalTodos}
                </span>
                {activeTab === 'todos' && totalComNotificacoes > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('mencoes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                activeTab === 'mencoes'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span className="flex items-center gap-2">
                <AtSign className="w-4 h-4" />
                Menções
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === 'mencoes'
                    ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  {totalMencoes}
                </span>
                {activeTab === 'mencoes' && totalComNotificacoes > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                  </span>
                )}
              </span>
            </button>
          </nav>
        </div>
        {totalComNotificacoes > 0 && (
          <button
            type="button"
            onClick={() => {
              setFilters((prev) => ({
                ...prev,
                temNotificacoes: prev.temNotificacoes === 'sim' ? 'all' : 'sim',
              }));
              setQuickFilter((prev) => (prev === 'unread' ? 'all' : 'unread'));
            }}
            className="w-full text-left bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30 border-b border-orange-200 dark:border-orange-800 px-6 py-3"
          >
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 dark:bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                {totalComNotificacoes} ticket(s) com novas respostas não lidas
                {quickFilter === 'unread' && ' (filtrando)'}
              </span>
            </div>
          </button>
        )}
        {totalDuplicados > 0 && (
          <button
            type="button"
            onClick={() => setQuickFilter((prev) => (prev === 'duplicados' ? 'all' : 'duplicados'))}
            className="w-full text-left bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 border-b border-amber-200 dark:border-amber-800 px-6 py-3"
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                {totalDuplicados} ticket(s) duplicado(s) detectado(s) - mesmo departamento, assunto, descrição e data de criação
                {quickFilter === 'duplicados' && ' (filtrando)'}
              </span>
            </div>
          </button>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Usuário
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  Criado Em
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                  Atualizado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((ticket) => (
                  <tr 
                    key={ticket.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 ${
                      ticketsComNotificacoes.has(ticket.id) 
                        ? 'bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/30 dark:to-yellow-900/30 border-l-4 border-l-orange-500 dark:border-l-orange-400 shadow-sm' 
                        : ''
                    } ${
                      ticket.eh_mencionado
                        ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-l-4 border-l-purple-500 dark:border-l-purple-400 shadow-sm'
                        : ''
                    } ${
                      ticket.eh_duplicado
                        ? 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 border-l-4 border-l-amber-500 dark:border-l-amber-400 shadow-sm'
                        : ''
                    }`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start gap-1">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:underline flex items-center gap-2 min-w-0 flex-1"
                          title={ticket.assunto || 'N/A'}
                        >
                          {ticket.tem_anexos === 1 && (
                            <span title="Possui anexos">
                              <Paperclip className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                            </span>
                          )}
                          {ticket.eh_mencionado && (
                            <span title="Você foi mencionado neste ticket">
                              <AtSign className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                            </span>
                          )}
                          {ticketsComNotificacoes.has(ticket.id) && (
                            <div className="flex-shrink-0 w-3 h-3 bg-orange-500 dark:bg-orange-400 rounded-full animate-pulse" title="Novas respostas não lidas"></div>
                          )}
                          {ticket.eh_duplicado && (
                            <span 
                              className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 bg-amber-500 dark:bg-amber-600 rounded-full" 
                              title="Ticket duplicado: mesmo departamento, assunto, descrição e data de criação"
                            >
                              <Copy className="w-3 h-3 text-white" />
                            </span>
                          )}
                          #{ticket.id}
                        </Link>
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="truncate max-w-xs text-[15px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                          title={ticket.assunto || 'N/A'}
                        >
                          {ticket.assunto || 'N/A'}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="flex flex-col items-start gap-1">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {ticket.usuario_nome || 'Usuário Desconhecido'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {ticket.unidade_nome || 'Escritório'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.prioridade_nome || '')}`}>
                        {ticket.prioridade_nome || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status_nome || '')}`}>
                        {ticket.status_nome || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1.5 text-gray-400 dark:text-gray-500" />
                        {formatDateWithTime(ticket.created_at || '')}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden xl:table-cell">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1.5 text-gray-400 dark:text-gray-500" />
                        {formatDateWithTime(ticket.updated_at || '')}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="inline-flex items-center justify-center p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-200 hover:scale-110 active:scale-95"
                          title="Visualizar ticket"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <div className="inline-flex items-center justify-center">
                        <FavoriteButton
                          ticketId={ticket.id}
                          size="sm"
                          variant="ghost"
                          showText={false}
                          showBorder={false}
                          isFavorite={ticketsFavoritos.has(ticket.id)}
                          onToggle={(isFavorite) => {
                            const newFavoritos = new Set(ticketsFavoritos);
                            if (isFavorite) {
                              newFavoritos.add(ticket.id);
                            } else {
                              newFavoritos.delete(ticket.id);
                            }
                            setTicketsFavoritos(newFavoritos);
                          }}
                        />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <Ticket className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-lg font-medium">Nenhum ticket encontrado</p>
                    <p className="text-sm">Tente ajustar os filtros de busca</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, totalItems)} de {totalItems} resultados
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                ← Anterior
              </button>

              <div className="flex items-center justify-center space-x-1 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${page === pagination.currentPage
                          ? 'bg-blue-600 dark:bg-blue-500 text-white border-blue-600 dark:border-blue-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === pagination.currentPage - 2 ||
                    page === pagination.currentPage + 2
                  ) {
                    return <span key={page} className="px-1 sm:px-2 text-gray-400 dark:text-gray-500 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === totalPages}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                Próximo →
              </button>
            </div>
          </div>
        </div>
      )}

      {tickets.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Mostrando {tickets.length} de {pagination.totalItems} ticket(s)
          </div>
        </div>
      )}

      <UnifiedFiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        unidades={unidades}
        tickets={tickets}
        usuario={usuario}
        usuarios={usuariosFiltro}
        onAdvancedSearch={handleAdvancedSearch}
        initialAdvancedFilters={filtrosBuscaAvancada}
        mode={filtersMode}
        onModeChange={setFiltersMode}
        useAdvancedSearch={filtersMode === 'advanced'}
      />
    </div>
  );
}
